import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import { fmtMoney } from '../money';
import { holdingUnsaved } from '../unsaved';
import Icon from './Icon';
import MoneyInput from './MoneyInput';

/**
 * ADJUST THE CONTRACT VALUE — a recorded decision, not a field edit (#435, R22).
 *
 * Changing an already-set contract value is a scope adjustment: it is gated to the
 * approver, it needs a reason, and it is written to History. So it is a FOCUSED
 * DIALOG, not a panel wedged into the budget form — the approver sees old → new →
 * delta at a glance, a required reason, and a line stating exactly what is recorded
 * and where (Vinnie approved the dialog, 8 Oct 2026; dev-preview/contract-value/03).
 *
 * The baseline follows the contract value. When the project holds a deliberate
 * contingency (its baseline sits below the contract), the reader CHOOSES on screen
 * whether the new contract moves the baseline in full or preserves that held offset
 * — never silently forced either way (#435 B).
 */
export default function ScopeAdjustDialog({
  projectName,
  from,
  baseline,
  onClose,
  onDone,
}: {
  projectName: string;
  /** The current contract value being changed. */
  from: number;
  /** The current budget baseline, to detect a held contingency offset. */
  baseline: number | undefined;
  onClose: () => void;
  /** The approved new contract value and the baseline it set, to reflect in the form. */
  onDone: (newValue: number, newBaseline: number) => void;
}) {
  const adjustContractValue = useApp((s) => s.adjustContractValue);
  const [next, setNext] = useState<number | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [doc, setDoc] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  // A held contingency — the baseline deliberately sits apart from the contract.
  const offset = baseline != null && baseline !== from ? from - baseline : 0;
  const hasOffset = offset !== 0;
  const [baselineMode, setBaselineMode] = useState<'full' | 'offset'>('full');

  // A typed value + the required reason are real work — a scope change is a
  // recorded CR narrative. So every dismissal path asks before binning a draft,
  // the IssueModal pattern, instead of silently dropping it (L91).
  const dirty = next != null || !!reason.trim() || !!doc.trim();
  useEffect(() => (dirty ? holdingUnsaved() : undefined), [dirty]);
  const requestClose = async () => {
    if (dirty) {
      const ok = await confirmDialog({
        title: 'Discard this scope change?',
        body: 'The new value and reason you entered will be lost.',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        tone: 'caution',
      });
      if (!ok) return;
    }
    onClose();
  };

  // Escape closes only this dialog (capture + stopPropagation), never a surface
  // behind it — the app-wide nested-overlay rule, as in CorrectionDialog.
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); requestClose(); } };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, onClose]);

  const delta = next != null ? next - from : 0;
  const pct = from !== 0 && next != null ? (delta / from) * 100 : 0;
  const sign = delta >= 0 ? '+' : '−'; // + / − (true minus)
  const deltaStr = next != null ? `${sign}${fmtMoney(Math.abs(delta))}` : '';
  const pctStr = next != null ? `${sign}${Math.abs(pct).toFixed(1)}%` : '';
  const newBaseline = next == null ? undefined : hasOffset && baselineMode === 'offset' ? next - offset : next;

  const canSubmit = next != null && next > 0 && next !== from && !!reason.trim() && !busy;

  const submit = async () => {
    if (!canSubmit || next == null || newBaseline == null) return;
    setBusy(true);
    setProblem('');
    const err = await adjustContractValue(next, reason, doc, newBaseline);
    setBusy(false);
    if (err) { setProblem(err); return; }
    onDone(next, newBaseline);
    onClose();
  };

  // Portal to the body so the dialog centres on the viewport, never on a transformed
  // parent (the budget surface can sit inside a drawer whose transform is a
  // containing block) — the CorrectionDialog portal rule.
  return createPortal(
    <div className="overlay" onClick={requestClose}>
      <div className="modal scope-adjust" role="dialog" aria-modal="true" aria-label="Adjust the contract value" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-head-title">Adjust the contract value</span>
          <button className="modal-head-close" aria-label="Close" onClick={requestClose}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="modal-body">
          <p className="sa-context">
            {projectName} &middot; changing the agreed total is a scope change &mdash; it needs a
            reason and is recorded against the project.
          </p>

          <div className="sa-delta">
            <span className="sa-delta-cell sa-delta-current">
              <span className="sa-delta-l">Current</span>
              <span className="sa-delta-v tnum">{fmtMoney(from)}</span>
            </span>
            <span className="sa-delta-arrow" aria-hidden="true">&rarr;</span>
            <span className="sa-delta-cell">
              <span className="sa-delta-l">New</span>
              <span className="sa-delta-v tnum">{next != null ? fmtMoney(next) : '—'}</span>
            </span>
            <span className="sa-delta-cell sa-delta-change">
              <span className="sa-delta-l">Change</span>
              <span className="sa-delta-v tnum">{next != null ? <>{deltaStr} &middot; {pctStr}</> : '—'}</span>
            </span>
          </div>

          <div className="fd-grid">
            <label className="stack">
              <span>New contract value <span className="req">*</span></span>
              <MoneyInput value={next} onChange={setNext} autoFocus required ariaLabel="New contract value" placeholder="e.g. 712000" />
            </label>
            <label className="stack">
              <span>Reason for the change <span className="req">*</span></span>
              <input className="input" value={reason} required aria-required="true" aria-label="Reason for the change" onChange={(e) => setReason(e.target.value)} placeholder="e.g. added the reporting module (CR-014)" />
            </label>
            <label className="stack fd-wide">
              <span>Signed document reference</span>
              <input className="input" value={doc} onChange={(e) => setDoc(e.target.value)} placeholder="A link or number for a signed variation, if there is one" />
            </label>
          </div>

          {hasOffset && next != null && (
            <div className="sa-contingency">
              <p className="sa-contingency-h">
                The baseline is held <b>{fmtMoney(Math.abs(offset))}</b> {offset > 0 ? 'below' : 'above'} the contract
                &mdash; a contingency. Where should the new contract leave it?
              </p>
              <label className="sa-opt">
                <input type="radio" name="sa-baseline" checked={baselineMode === 'full'} onChange={() => setBaselineMode('full')} />
                <span>Move the baseline to the full new contract &mdash; <b className="tnum">{fmtMoney(next)}</b></span>
              </label>
              <label className="sa-opt">
                <input type="radio" name="sa-baseline" checked={baselineMode === 'offset'} onChange={() => setBaselineMode('offset')} />
                <span>Keep the held contingency &mdash; baseline becomes <b className="tnum">{fmtMoney(next - offset)}</b></span>
              </label>
            </div>
          )}

          {problem && <p className="id-problem">{problem}</p>}

          <p className="sa-recorded">
            <Icon name="shield" size={16} />
            <span>
              Recorded in the project&rsquo;s History, with the approver and the reason. The budget baseline moves
              to <b className="tnum">{newBaseline != null ? fmtMoney(newBaseline) : fmtMoney(from)}</b>.
            </span>
          </p>
        </div>

        <div className="modal-actions">
          <button className="btn btn-small" onClick={requestClose}>Cancel</button>
          <button className="btn btn-primary btn-small" disabled={!canSubmit} onClick={submit}>Record scope change</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
